/**
 * MemberManagementTab.jsx
 * Full portal member management for admin panel.
 * Features: portal list with counts, member table, role change, suspend/unsuspend,
 * remove (soft delete), bulk actions, add member (email / pending / CSV import).
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  getFirestore, collection, doc, updateDoc, addDoc, setDoc, getDocs,
  onSnapshot, query, orderBy, serverTimestamp, where, limit, Timestamp,
} from 'firebase/firestore';
import { writeLog } from '../../../utils/firebaseUtils';
import PrivilegedChangeModal, { PRIVILEGED_ROLES } from '../../PrivilegedChangeModal/PrivilegedChangeModal';
import { bumpVersion } from '../../../utils/versionUtils';
import { PORTAL_CARDS } from '../../../constants/portalCards';
import { rolesFromDepartments } from '../../../utils/portalRegistry';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };
const ADMIN_NAME = () => {
  try { return getAuth(getApp()).currentUser?.displayName || 'Admin'; } catch (_) { return 'Admin'; }
};

export const ALL_ROLES = [
  { id: 'admin',        label: 'Admin',                color: '#0e9cc0' },
  { id: 'c_suite',      label: 'C-Suite',              color: '#ef4444' },
  { id: 'payload',      label: 'Payloads',             color: '#0891b2' },
  { id: 'ops',          label: 'Operations',           color: '#e11d48' },
  { id: 'mp',           label: 'Mission Planning',     color: '#6366f1' },
  { id: 'embedded',     label: 'Embedded Systems',     color: '#f59e0b' },
  { id: 'system',       label: 'Systems Engineering',  color: '#10b981' },
  { id: 'experimental', label: 'Experimental Results', color: '#06b6d4' },
  { id: 'finance',      label: 'Finance & Admin',      color: '#34d399' },
  { id: 'mentor',       label: 'Mentor / Guest',       color: '#a855f7' },
  { id: 'pe',           label: 'Prompt Engineering',   color: '#8b5cf6' },
  { id: 'obc',          label: 'OBC Development',      color: '#00FFB2' },
];

const roleColor = r => ALL_ROLES.find(x => x.id === r)?.color || '#64748b';
const roleLabel = r => ALL_ROLES.find(x => x.id === r)?.label || r;

function relTime(ts) {
  if (!ts?.toDate) return 'Never';
  const diff = Date.now() - ts.toDate().getTime();
  const min = Math.floor(diff / 60000);
  if (min < 2)  return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)  return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7)    return `${d}d ago`;
  return ts.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// Stitch dark tokens
const S = {
  bg:      'var(--smc-bg, #070c1a)',
  panel:   'var(--smc-panel, #0d1526)',
  input:   'var(--smc-deep, #060b18)',
  border:  'var(--smc-bd, rgba(255,255,255,0.08))',
  faint:   'var(--smc-hov, rgba(255,255,255,0.04))',
  text:    'var(--smc-tx, #d9e2ff)',
  muted:   'var(--smc-tx2, #8892a4)',
  dim:     'var(--smc-tx3, #4d5a6e)',
  primary: 'var(--smc-ac, #65d4fa)',
  green:   'var(--smc-gn, #34d399)',
  red:     'var(--smc-rd, #f87171)',
  yellow:  'var(--smc-am, #fbbf24)',
  purple:  'var(--smc-pu, #a78bfa)',
  mono: "'Share Tech Mono','Courier New',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

function ExpiryBadge({ expiresAt }) {
  if (!expiresAt?.toDate) return <span style={{ color: S.dim, fontFamily: S.mono, fontSize: 10 }}>—</span>;
  const date     = expiresAt.toDate();
  const daysLeft = Math.ceil((date - Date.now()) / 86400000);
  if (daysLeft < 0) return (
    <span style={{ fontFamily: S.mono, fontSize: 9, color: S.red,
      background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)',
      padding: '1px 7px', borderRadius: 4 }}>EXPIRED</span>
  );
  if (daysLeft <= 7) return (
    <span style={{ fontFamily: S.mono, fontSize: 9, color: S.red }}>{daysLeft}d left</span>
  );
  if (daysLeft <= 14) return (
    <span style={{ fontFamily: S.mono, fontSize: 9, color: S.yellow }}>{daysLeft}d left</span>
  );
  return (
    <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>
      {date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
    </span>
  );
}

function Avatar({ name, color, size = 30 }) {
  const c = color || S.primary;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `${c}22`, border: `1px solid ${c}55`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: S.mono, fontSize: size * 0.35, color: c, flexShrink: 0,
    }}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MemberManagementTab({ defaultPortal = 'all' }) {
  const db = DB();

  const [members,        setMembers]        = useState([]);
  const [privChange,     setPrivChange]     = useState(null); // founder-verified role grant
  const [pending,        setPending]        = useState([]);
  const [customPortals,  setCustomPortals]  = useState([]);
  const [customDepts,    setCustomDepts]    = useState([]);
  const [selectedPortal, setSelectedPortal] = useState(defaultPortal);
  const [search,         setSearch]         = useState('');
  const [selected,       setSelected]       = useState(new Set());
  const [hoverRow,       setHoverRow]       = useState(null);
  const [editRoleFor,    setEditRoleFor]    = useState(null);
  const [suspendModal,   setSuspendModal]   = useState(null);
  const [removeModal,    setRemoveModal]    = useState(null);
  const [addModal,       setAddModal]       = useState(false);
  const [renewModal,     setRenewModal]     = useState(null);
  const [historyModal,   setHistoryModal]   = useState(null);
  const [historyData,    setHistoryData]    = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [busyUid,        setBusyUid]        = useState(null);
  const [toast,          setToast]          = useState('');

  useEffect(() => {
    if (!db) return;
    const u1 = onSnapshot(
      query(collection(db, 'authorized_users'), orderBy('name')),
      snap => setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() }))),
      () => {}
    );
    const u2 = onSnapshot(
      query(collection(db, 'access_requests'), where('status', '==', 'pending')),
      snap => setPending(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    const u3 = onSnapshot(collection(db, 'custom_portals'),
      snap => setCustomPortals(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    const u4 = onSnapshot(collection(db, 'departments'),
      snap => setCustomDepts(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => { u1(); u2(); u3(); u4(); };
  }, [db]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveRoles = useMemo(() => {
    const extra = rolesFromDepartments(customDepts);
    const ids = new Set(ALL_ROLES.map(r => r.id));
    return [...ALL_ROLES, ...extra.filter(r => !ids.has(r.id))];
  }, [customDepts]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3200); }

  const portalList = [
    { id: 'all', label: 'All Members', icon: '👥', color: S.primary },
    ...PORTAL_CARDS.map(p => ({ id: p.id, label: p.label, icon: p.icon, color: p.color })),
    ...customDepts.map(d => ({ id: d.id, label: d.name || d.id, icon: '📦', color: d.color || '#64748b' })),
    ...customPortals.map(p => ({ id: p.id, label: p.label || p.id, icon: '🔷', color: '#64748b' })),
  ];

  const countFor = id =>
    id === 'all'
      ? members.filter(m => m.active !== false).length
      : members.filter(m => m.role === id && m.active !== false).length;

  const portalMembers = useMemo(() => {
    let list = members;
    if (selectedPortal !== 'all') list = list.filter(m => m.role === selectedPortal);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [members, selectedPortal, search]);

  const allSelected = portalMembers.length > 0 && portalMembers.every(m => selected.has(m.uid));

  // ── Actions ─────────────────────────────────────────────────────────────────
  async function changeRole(member, newRole) {
    if (!db || !newRole || newRole === member.role) { setEditRoleFor(null); return; }
    if (PRIVILEGED_ROLES.includes(newRole)) {
      setEditRoleFor(null);
      setPrivChange({ targetUid: member.uid, targetName: member.name || member.email, changeType: 'role', value: newRole });
      return;
    }
    setBusyUid(member.uid);
    try {
      const oldRole = member.role;
      await updateDoc(doc(db, 'authorized_users', member.uid), {
        role: newRole,
        portalHistory: [...(member.portalHistory || []), oldRole],
      });
      await writeLog('role_changed', {
        action: 'role_changed', from: oldRole, to: newRole,
        targetUser: member.name, changedBy: ADMIN_NAME(),
      });
      await addDoc(collection(db, 'notifications'), {
        recipientId: member.uid, type: 'role_changed',
        message: `Your portal access has been updated to ${roleLabel(newRole)} by ${ADMIN_NAME()}.`,
        createdAt: serverTimestamp(), read: false,
      });
      await bumpVersion(`Role changed: ${member.name} → ${roleLabel(newRole)}`, ADMIN_NAME());
      showToast(`✓ ${member.name} moved to ${roleLabel(newRole)}`);
    } finally { setBusyUid(null); setEditRoleFor(null); }
  }

  async function suspendMember(member, reason) {
    if (!db) return;
    setBusyUid(member.uid);
    try {
      await updateDoc(doc(db, 'authorized_users', member.uid), {
        suspended: true, suspendedReason: reason,
        suspendedAt: serverTimestamp(), suspendedBy: ADMIN_NAME(),
      });
      await writeLog('member_suspended', {
        action: 'member_suspended', targetUser: member.name,
        reason, suspendedBy: ADMIN_NAME(),
      });
      showToast(`${member.name} suspended`);
    } finally { setBusyUid(null); setSuspendModal(null); }
  }

  async function unsuspendMember(member) {
    if (!db) return;
    setBusyUid(member.uid);
    try {
      await updateDoc(doc(db, 'authorized_users', member.uid), {
        suspended: false, suspendedReason: null, suspendedAt: null, suspendedBy: null,
      });
      await writeLog('member_unsuspended', { action: 'member_unsuspended', targetUser: member.name, by: ADMIN_NAME() });
      showToast(`✓ ${member.name} unsuspended`);
    } finally { setBusyUid(null); }
  }

  async function removeMember(member) {
    if (!db) return;
    setBusyUid(member.uid);
    try {
      await updateDoc(doc(db, 'authorized_users', member.uid), { active: false });
      await writeLog('member_removed', {
        action: 'member_removed', targetUser: member.name,
        email: member.email, removedBy: ADMIN_NAME(),
      });
      showToast(`${member.name} removed`);
    } finally { setBusyUid(null); setRemoveModal(null); }
  }

  async function renewMember(member, days) {
    if (!db) return;
    setBusyUid(member.uid);
    try {
      const newExpiry = Timestamp.fromDate(new Date(Date.now() + days * 86400000));
      await updateDoc(doc(db, 'authorized_users', member.uid), {
        expiresAt: newExpiry, active: true,
        deactivatedAt: null, deactivatedReason: null,
      });
      await writeLog('access_renewed', {
        action: 'access_renewed', targetUser: member.name,
        renewedBy: ADMIN_NAME(), daysAdded: days,
      });
      await addDoc(collection(db, 'notifications'), {
        recipientId: member.uid, type: 'access_renewed',
        message: `Your access has been renewed for ${days} more days by ${ADMIN_NAME()}.`,
        createdAt: serverTimestamp(), read: false,
      });
      showToast(`✓ ${member.name} access renewed for ${days} days`);
    } finally { setBusyUid(null); setRenewModal(null); }
  }

  async function openHistory(member) {
    setHistoryModal(member);
    if (historyData[member.uid]) return;
    setLoadingHistory(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'access_logs'),
        where('email', '==', member.email),
        orderBy('timestamp', 'desc'),
        limit(15)
      ));
      setHistoryData(p => ({ ...p, [member.uid]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    } catch(_) {
      setHistoryData(p => ({ ...p, [member.uid]: [] }));
    }
    setLoadingHistory(false);
  }

  async function restoreMember(member) {
    if (!db) return;
    setBusyUid(member.uid);
    try {
      await updateDoc(doc(db, 'authorized_users', member.uid), { active: true });
      await writeLog('member_restored', { action: 'member_restored', targetUser: member.name, by: ADMIN_NAME() });
      showToast(`✓ ${member.name} restored`);
    } finally { setBusyUid(null); }
  }

  // ── Bulk actions ─────────────────────────────────────────────────────────────
  async function bulkChangeRole(newRole) {
    if (!db || !newRole) return;
    if (PRIVILEGED_ROLES.includes(newRole)) {
      showToast(`${roleLabel(newRole)} needs founder verification — assign it one member at a time`);
      return;
    }
    for (const uid of selected) {
      const m = members.find(x => x.uid === uid);
      if (!m || m.role === newRole) continue;
      await updateDoc(doc(db, 'authorized_users', uid), {
        role: newRole, portalHistory: [...(m.portalHistory || []), m.role],
      });
    }
    showToast(`${selected.size} member(s) moved to ${roleLabel(newRole)}`);
    setSelected(new Set());
  }

  async function bulkSuspend() {
    if (!db) return;
    for (const uid of selected) {
      await updateDoc(doc(db, 'authorized_users', uid), {
        suspended: true, suspendedAt: serverTimestamp(),
        suspendedBy: ADMIN_NAME(), suspendedReason: 'Bulk suspension by admin',
      });
    }
    showToast(`${selected.size} member(s) suspended`);
    setSelected(new Set());
  }

  async function bulkRemove() {
    if (!window.confirm(`Remove ${selected.size} member(s)? They can be restored later.`)) return;
    if (!db) return;
    for (const uid of selected) {
      await updateDoc(doc(db, 'authorized_users', uid), { active: false });
      await writeLog('member_removed', { action: 'member_removed', removedBy: ADMIN_NAME(), targetUid: uid });
    }
    showToast(`${selected.size} member(s) removed`);
    setSelected(new Set());
  }

  function exportCSV() {
    const rows = portalMembers.map(m => [
      m.name, m.email, m.role,
      m.suspended ? 'suspended' : m.active === false ? 'removed' : 'active',
      relTime(m.lastActive),
    ]);
    const csv = ['name,email,role,status,last_active',
      ...rows.map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `members_${selectedPortal}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }

  const selPortalInfo = portalList.find(p => p.id === selectedPortal) || portalList[0];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: S.bg }}>
      {privChange && (
        <PrivilegedChangeModal
          change={privChange}
          requestedBy={{ uid: getAuth(getApp()).currentUser?.uid, name: ADMIN_NAME() }}
          onApplied={() => { showToast(`✓ ${privChange.targetName} is now ${roleLabel(privChange.value)}`); setPrivChange(null); }}
          onClose={() => setPrivChange(null)} />
      )}

      {/* ── Left: Portal list ─────────────────────────────────────────────── */}
      <div style={{ width: 228, flexShrink: 0, borderRight: `1px solid ${S.border}`,
        background: '#06132b', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px 8px', fontFamily: S.mono, fontSize: 9,
          color: S.dim, letterSpacing: '0.15em', textTransform: 'uppercase', borderBottom: `1px solid ${S.faint}` }}>
          PORTALS
        </div>
        {portalList.map(p => {
          const cnt = countFor(p.id);
          const active = selectedPortal === p.id;
          return (
            <button key={p.id}
              onClick={() => { setSelectedPortal(p.id); setSearch(''); setSelected(new Set()); }}
              style={{ width: '100%', background: active ? `${p.color}10` : 'transparent',
                border: 'none', borderLeft: `2px solid ${active ? p.color : 'transparent'}`,
                display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px',
                cursor: 'pointer', textAlign: 'left', transition: 'all 0.1s' }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: 13, flexShrink: 0 }}>{p.icon}</span>
              <span style={{ fontFamily: S.sans, fontSize: 12, color: active ? p.color : S.muted,
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.label}
              </span>
              {cnt > 0 && (
                <span style={{ fontFamily: S.mono, fontSize: 9,
                  color: active ? p.color : S.dim,
                  background: active ? `${p.color}22` : 'rgba(255,255,255,0.04)',
                  padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Right: Member area ────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${S.border}`,
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: S.sans, fontSize: 14, fontWeight: 600, color: S.text }}>
              {selPortalInfo.label}
            </div>
            <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: '0.1em', marginTop: 2 }}>
              {portalMembers.length} MEMBER{portalMembers.length !== 1 ? 'S' : ''}
              {search ? ' · FILTERED' : ''}
            </div>
          </div>
          <button onClick={exportCSV}
            style={{ background: 'transparent', border: `1px solid ${S.border}`,
              color: S.dim, fontFamily: S.mono, fontSize: 9, textTransform: 'uppercase',
              letterSpacing: '0.05em', padding: '6px 12px', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = S.primary; e.currentTarget.style.color = S.primary; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.color = S.dim; }}>
            ↓ CSV
          </button>
          <button onClick={() => setAddModal(true)}
            style={{ background: '#0e9cc0', color: '#060b18', border: 'none',
              fontFamily: S.mono, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', padding: '8px 16px', cursor: 'pointer' }}>
            + ADD MEMBER
          </button>
        </div>

        {/* Search + bulk action bar */}
        <div style={{ padding: '8px 18px', borderBottom: `1px solid ${S.border}`,
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: S.dim, fontSize: 14, pointerEvents: 'none' }}>⌕</span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`,
                color: S.text, fontFamily: S.mono, fontSize: 11, padding: '7px 12px 7px 30px',
                outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {selected.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: S.mono, fontSize: 10, color: S.primary, flexShrink: 0 }}>
                {selected.size} selected
              </span>
              <div style={{ position: 'relative' }}>
                <select onChange={e => { if (e.target.value) { bulkChangeRole(e.target.value); e.target.value = ''; } }}
                  style={{ appearance: 'none', background: S.input, border: `1px solid ${S.border}`,
                    color: S.muted, fontFamily: S.mono, fontSize: 9, padding: '5px 22px 5px 8px',
                    cursor: 'pointer', outline: 'none', textTransform: 'uppercase' }}>
                  <option value="">MOVE TO…</option>
                  {effectiveRoles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
                <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  color: S.dim, fontSize: 10, pointerEvents: 'none' }}>▾</span>
              </div>
              <button onClick={bulkSuspend}
                style={{ fontFamily: S.mono, fontSize: 9, padding: '5px 9px', cursor: 'pointer',
                  background: '#fbbf2420', border: '1px solid #fbbf2444', color: '#fbbf24', textTransform: 'uppercase' }}>
                SUSPEND
              </button>
              <button onClick={bulkRemove}
                style={{ fontFamily: S.mono, fontSize: 9, padding: '5px 9px', cursor: 'pointer',
                  background: '#f8717120', border: '1px solid #f8717144', color: '#f87171', textTransform: 'uppercase' }}>
                REMOVE
              </button>
              <button onClick={() => setSelected(new Set())}
                style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
            </div>
          )}
        </div>

        {/* Member table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {portalMembers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px',
              fontFamily: S.mono, fontSize: 11, color: S.dim }}>
              {members.length === 0
                ? 'No members in system yet.'
                : 'No members match your search.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${S.border}`, background: 'rgba(255,255,255,0.02)' }}>
                  <th style={{ width: 36, padding: '9px 8px 9px 16px' }}>
                    <input type="checkbox" checked={allSelected}
                      onChange={() => allSelected
                        ? setSelected(new Set())
                        : setSelected(new Set(portalMembers.map(m => m.uid)))}
                      style={{ accentColor: S.primary, cursor: 'pointer' }} />
                  </th>
                  {['MEMBER', 'ROLE', 'STATUS', 'LAST ACTIVE', 'EXPIRY', ''].map((col, i) => (
                    <th key={i} style={{ padding: '9px 12px', textAlign: 'left',
                      fontFamily: S.mono, fontSize: 9, color: S.dim,
                      letterSpacing: '0.12em', fontWeight: 400,
                      textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {portalMembers.map(m => {
                  const rc         = roleColor(m.role);
                  const isBusy     = busyUid === m.uid;
                  const isHover    = hoverRow === m.uid;
                  const isChosen   = selected.has(m.uid);
                  const isSuspended = m.suspended === true;
                  const isRemoved  = m.active === false;
                  const statusCol  = isRemoved ? '#64748b' : isSuspended ? '#fbbf24' : '#34d399';
                  const statusTxt  = isRemoved ? 'Removed' : isSuspended ? 'Suspended' : 'Active';

                  return (
                    <tr key={m.uid}
                      onMouseEnter={() => setHoverRow(m.uid)}
                      onMouseLeave={() => setHoverRow(null)}
                      style={{
                        borderBottom: `1px solid ${S.faint}`,
                        background: isChosen
                          ? 'rgba(101,212,250,0.04)'
                          : isHover
                            ? 'rgba(255,255,255,0.015)'
                            : 'transparent',
                        opacity: isRemoved ? 0.55 : 1,
                        transition: 'background 0.1s',
                      }}>
                      {/* Checkbox */}
                      <td style={{ padding: '10px 8px 10px 16px', width: 36 }}>
                        <input type="checkbox" checked={isChosen}
                          onChange={() => {
                            const next = new Set(selected);
                            if (isChosen) next.delete(m.uid); else next.add(m.uid);
                            setSelected(next);
                          }}
                          style={{ accentColor: S.primary, cursor: 'pointer' }} />
                      </td>

                      {/* Member info */}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={m.name} color={rc} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontFamily: S.sans, fontSize: 13, fontWeight: 600,
                              color: S.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {m.name || '—'}
                            </div>
                            <div style={{ fontFamily: S.mono, fontSize: 10, color: S.dim,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {m.email || '—'}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {editRoleFor === m.uid ? (
                          <select autoFocus defaultValue={m.role}
                            onChange={e => changeRole(m, e.target.value)}
                            onBlur={() => setEditRoleFor(null)}
                            style={{ appearance: 'none', background: S.input,
                              border: `1px solid ${rc}66`, color: rc,
                              fontFamily: S.mono, fontSize: 10, padding: '4px 8px',
                              cursor: 'pointer', outline: 'none' }}>
                            {effectiveRoles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                        ) : (
                          <span style={{ fontFamily: S.mono, fontSize: 10, color: rc,
                            background: `${rc}18`, border: `1px solid ${rc}44`,
                            padding: '2px 10px', borderRadius: 999 }}>
                            {roleLabel(m.role)}
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusCol, flexShrink: 0 }} />
                          <span style={{ fontFamily: S.mono, fontSize: 10, color: statusCol }}>{statusTxt}</span>
                        </div>
                        {isSuspended && m.suspendedReason && (
                          <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, marginTop: 2 }}>
                            {m.suspendedReason.slice(0, 34)}{m.suspendedReason.length > 34 ? '…' : ''}
                          </div>
                        )}
                      </td>

                      {/* Last active */}
                      <td style={{ padding: '10px 12px', fontFamily: S.mono, fontSize: 10, color: S.dim, whiteSpace: 'nowrap' }}>
                        {relTime(m.lastActive)}
                      </td>

                      {/* Expiry */}
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <ExpiryBadge expiresAt={m.expiresAt} />
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '10px 16px 10px 4px', whiteSpace: 'nowrap' }}>
                        {isBusy ? (
                          <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>…</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4,
                            opacity: isHover ? 1 : 0, transition: 'opacity 0.15s' }}>
                            {!isRemoved ? (
                              <>
                                <button onClick={() => setEditRoleFor(m.uid)}
                                  title="Change role"
                                  style={{ background: 'none', border: `1px solid ${S.border}`,
                                    color: S.muted, fontFamily: S.mono, fontSize: 9,
                                    padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = S.primary; e.currentTarget.style.color = S.primary; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.color = S.muted; }}>
                                  ROLE
                                </button>
                                {isSuspended ? (
                                  <button onClick={() => unsuspendMember(m)}
                                    style={{ background: 'none', border: '1px solid #34d39944',
                                      color: '#34d399', fontFamily: S.mono, fontSize: 9,
                                      padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                    UNSUSPEND
                                  </button>
                                ) : (
                                  <button onClick={() => setSuspendModal(m)}
                                    style={{ background: 'none', border: '1px solid #fbbf2444',
                                      color: '#fbbf24', fontFamily: S.mono, fontSize: 9,
                                      padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                    SUSPEND
                                  </button>
                                )}
                                <button onClick={() => setRemoveModal(m)}
                                  style={{ background: 'none', border: '1px solid #f8717144',
                                    color: '#f87171', fontFamily: S.mono, fontSize: 9,
                                    padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                  REMOVE
                                </button>
                                <button onClick={() => setRenewModal(m)}
                                  style={{ background: 'none', border: '1px solid #a78bfa44',
                                    color: '#a78bfa', fontFamily: S.mono, fontSize: 9,
                                    padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                  RENEW
                                </button>
                              </>
                            ) : (
                              <button onClick={() => restoreMember(m)}
                                style={{ background: 'none', border: '1px solid #34d39944',
                                  color: '#34d399', fontFamily: S.mono, fontSize: 9,
                                  padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                RESTORE
                              </button>
                            )}
                            <button onClick={() => openHistory(m)}
                              style={{ background: 'none', border: `1px solid ${S.border}`,
                                color: S.dim, fontFamily: S.mono, fontSize: 9,
                                padding: '3px 7px', cursor: 'pointer', textTransform: 'uppercase' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = S.primary; e.currentTarget.style.color = S.primary; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = S.border; e.currentTarget.style.color = S.dim; }}>
                              HISTORY
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Table footer count */}
        <div style={{ padding: '8px 18px', borderTop: `1px solid ${S.faint}`, flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, textTransform: 'uppercase' }}>
            Showing {portalMembers.length} of {members.filter(m => m.active !== false).length} active members
          </span>
          <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>
            {members.filter(m => m.suspended).length} suspended · {members.filter(m => m.active === false).length} removed
          </span>
        </div>
      </div>

      {/* ── Toast ────────────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          zIndex: 2000, background: '#0d1526', border: '1px solid rgba(101,212,250,0.3)',
          padding: '10px 22px', fontFamily: S.mono, fontSize: 11, color: S.primary,
          boxShadow: '0 6px 24px rgba(0,0,0,0.6)', whiteSpace: 'nowrap' }}>
          {toast}
        </div>
      )}

      {/* ── Suspend modal ─────────────────────────────────────────────────────── */}
      {suspendModal && (
        <SuspendModal member={suspendModal}
          onConfirm={reason => suspendMember(suspendModal, reason)}
          onClose={() => setSuspendModal(null)} />
      )}

      {/* ── Remove modal ──────────────────────────────────────────────────────── */}
      {removeModal && (
        <RemoveModal member={removeModal}
          onConfirm={() => removeMember(removeModal)}
          onClose={() => setRemoveModal(null)} />
      )}

      {/* ── Renew access modal ───────────────────────────────────────────────── */}
      {renewModal && (
        <RenewModal member={renewModal}
          onConfirm={days => renewMember(renewModal, days)}
          onClose={() => setRenewModal(null)} />
      )}

      {/* ── Login history modal ───────────────────────────────────────────────── */}
      {historyModal && (
        <LoginHistoryModal
          member={historyModal}
          logs={historyData[historyModal.uid] || []}
          loading={loadingHistory}
          onClose={() => setHistoryModal(null)} />
      )}

      {/* ── Add member modal ──────────────────────────────────────────────────── */}
      {addModal && (
        <AddMemberModal
          db={db} pending={pending}
          roleOptions={effectiveRoles}
          defaultRole={selectedPortal !== 'all' ? selectedPortal : 'mp'}
          onClose={() => setAddModal(false)}
          onDone={msg => { setAddModal(false); showToast(msg); }} />
      )}
    </div>
  );
}

// ── SuspendModal ───────────────────────────────────────────────────────────────
function SuspendModal({ member, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(2,10,30,0.88)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: S.panel, border: '1px solid rgba(251,191,36,0.25)',
        width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 64px rgba(0,0,0,0.75)' }}>
        <div style={{ fontFamily: S.mono, fontSize: 14, color: '#fbbf24', marginBottom: 4 }}>SUSPEND ACCOUNT</div>
        <div style={{ fontFamily: S.sans, fontSize: 13, color: S.muted, marginBottom: 16, lineHeight: 1.6 }}>
          Suspending <strong style={{ color: S.text }}>{member.name}</strong>. They will not be able to log in until unsuspended.
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, textTransform: 'uppercase',
          letterSpacing: '0.1em', marginBottom: 6 }}>Reason (admin note — not shown to user)</div>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="E.g. Inactive for 30 days, policy violation…"
          style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`,
            color: S.text, fontFamily: S.mono, fontSize: 11, padding: '10px 12px',
            boxSizing: 'border-box', resize: 'vertical', outline: 'none' }} />
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={onClose}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${S.border}`,
              color: S.dim, fontFamily: S.mono, fontSize: 10, padding: '10px', cursor: 'pointer',
              textTransform: 'uppercase' }}>
            CANCEL
          </button>
          <button disabled={busy}
            onClick={async () => { setBusy(true); await onConfirm(reason); setBusy(false); }}
            style={{ flex: 2, background: '#fbbf2420', border: '1px solid #fbbf24', color: '#fbbf24',
              fontFamily: S.mono, fontSize: 10, fontWeight: 700, padding: '10px',
              cursor: busy ? 'wait' : 'pointer', textTransform: 'uppercase', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'SUSPENDING…' : 'CONFIRM SUSPENSION'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RemoveModal ────────────────────────────────────────────────────────────────
function RemoveModal({ member, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(2,10,30,0.88)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: S.panel, border: '1px solid rgba(248,113,113,0.25)',
        width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 64px rgba(0,0,0,0.75)' }}>
        <div style={{ fontFamily: S.mono, fontSize: 14, color: '#f87171', marginBottom: 4 }}>REMOVE MEMBER</div>
        <div style={{ fontFamily: S.sans, fontSize: 13, color: S.muted, marginBottom: 10, lineHeight: 1.65 }}>
          This will permanently revoke <strong style={{ color: S.text }}>{member.name}</strong>'s access.
          Their reports and experiments will be preserved. They must re-request to rejoin.
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim,
          background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.15)',
          padding: '8px 12px', marginBottom: 18 }}>
          ⚠ Soft delete — account data is preserved. Restorable from the table.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${S.border}`,
              color: S.dim, fontFamily: S.mono, fontSize: 10, padding: '10px',
              cursor: 'pointer', textTransform: 'uppercase' }}>
            CANCEL
          </button>
          <button disabled={busy}
            onClick={async () => { setBusy(true); await onConfirm(); }}
            style={{ flex: 2, background: '#f8717120', border: '1px solid #f87171', color: '#f87171',
              fontFamily: S.mono, fontSize: 10, fontWeight: 700, padding: '10px',
              cursor: busy ? 'wait' : 'pointer', textTransform: 'uppercase', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'REMOVING…' : 'REVOKE ACCESS'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RenewModal ─────────────────────────────────────────────────────────────────
function RenewModal({ member, onConfirm, onClose }) {
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  const newDate = new Date(Date.now() + days * 86400000);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(2,10,30,0.88)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: S.panel, border: '1px solid rgba(167,139,250,0.25)',
        width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 64px rgba(0,0,0,0.75)' }}>
        <div style={{ fontFamily: S.mono, fontSize: 14, color: '#a78bfa', marginBottom: 4 }}>RENEW ACCESS</div>
        <div style={{ fontFamily: S.sans, fontSize: 13, color: S.muted, marginBottom: 18, lineHeight: 1.6 }}>
          Extend access for <strong style={{ color: S.text }}>{member.name}</strong>.
          {member.expiresAt?.toDate && (
            <span style={{ fontFamily: S.mono, fontSize: 10, color: S.dim, display:'block', marginTop:4 }}>
              Current expiry: {member.expiresAt.toDate().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}
            </span>
          )}
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, textTransform: 'uppercase',
          letterSpacing: '0.1em', marginBottom: 8 }}>Extend by (days)</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[30, 60, 90, 180, 365].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{ flex: 1, padding: '8px 4px', fontFamily: S.mono, fontSize: 10,
                background: days === d ? 'rgba(167,139,250,0.15)' : 'transparent',
                border: `1px solid ${days === d ? '#a78bfa' : S.border}`,
                color: days === d ? '#a78bfa' : S.dim, cursor: 'pointer' }}>
              {d}d
            </button>
          ))}
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 10, color: S.dim, marginBottom: 18, lineHeight: 1.6 }}>
          New expiry: <span style={{ color: S.text }}>
            {newDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${S.border}`,
              color: S.dim, fontFamily: S.mono, fontSize: 10, padding: '10px',
              cursor: 'pointer', textTransform: 'uppercase' }}>
            CANCEL
          </button>
          <button disabled={busy}
            onClick={async () => { setBusy(true); await onConfirm(days); }}
            style={{ flex: 2, background: 'rgba(167,139,250,0.15)', border: '1px solid #a78bfa',
              color: '#a78bfa', fontFamily: S.mono, fontSize: 10, fontWeight: 700, padding: '10px',
              cursor: busy ? 'wait' : 'pointer', textTransform: 'uppercase', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'RENEWING…' : `RENEW +${days} DAYS`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LoginHistoryModal ──────────────────────────────────────────────────────────
function LoginHistoryModal({ member, logs, loading, onClose }) {
  const ACTION_COLOR = {
    login: '#34d399', login_google: '#34d399', login_phone: '#34d399',
    login_failed: '#f87171',
  };
  function fmtTs(ts) {
    if (!ts?.toDate) return '—';
    return ts.toDate().toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(2,10,30,0.88)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: S.panel, border: `1px solid ${S.border}`,
        width: '100%', maxWidth: 500, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.75)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${S.faint}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: S.mono, fontSize: 12, color: S.primary }}>LOGIN HISTORY</div>
            <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, marginTop: 2 }}>{member.name} · {member.email}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 32, fontFamily: S.mono, fontSize: 11, color: S.dim }}>Loading…</div>
          )}
          {!loading && logs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, fontFamily: S.mono, fontSize: 11, color: S.dim }}>
              No login records found for this account.
            </div>
          )}
          {logs.map(log => (
            <div key={log.id} style={{ display: 'flex', gap: 12, padding: '9px 0',
              borderBottom: `1px solid ${S.faint}`, alignItems: 'center' }}>
              <span style={{ fontFamily: S.mono, fontSize: 8, color: S.dim, width: 120, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {fmtTs(log.timestamp)}
              </span>
              <span style={{ fontFamily: S.mono, fontSize: 9,
                color: ACTION_COLOR[log.action] || S.dim,
                background: `${ACTION_COLOR[log.action] || S.dim}15`,
                padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                {log.action}
              </span>
              <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, flex: 1 }}>
                {log.role ? `role: ${log.role}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── AddMemberModal ─────────────────────────────────────────────────────────────
function AddMemberModal({ db, pending, roleOptions = ALL_ROLES, defaultRole, onClose, onDone }) {
  const [method,           setMethod]           = useState('email');
  const [name,             setName]             = useState('');
  const [email,            setEmail]            = useState('');
  const [role,             setRole]             = useState(defaultRole || 'mp');
  const [selectedPending,  setSelectedPending]  = useState(new Set());
  const [pendingRoles,     setPendingRoles]      = useState({});
  const [csvRows,          setCsvRows]          = useState([]);
  const [csvPreview,       setCsvPreview]       = useState(false);
  const [busy,             setBusy]             = useState(false);
  const [err,              setErr]              = useState('');
  const fileRef = useRef();

  function parseCSV(text) {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
    return lines.slice(1).map(line => {
      const vals = []; let cur = '', inQ = false;
      for (const ch of line + ',') {
        if (ch === '"') { inQ = !inQ; }
        else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
        else { cur += ch; }
      }
      const row = {};
      headers.forEach((h, i) => { row[h] = (vals[i] || '').replace(/^"|"$/g, '').trim(); });
      row._valid = !!(row.name && row.email && ALL_ROLES.some(r => r.id === row.role));
      return row;
    });
  }

  function handleFile(e) {
    const f = e.target.files[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => { setCsvRows(parseCSV(ev.target.result)); setCsvPreview(true); };
    reader.readAsText(f);
    e.target.value = '';
  }

  function downloadTemplate() {
    const csv = 'name,email,role\nJohn Doe,john@example.com,mp\nJane Smith,jane@example.com,embedded';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'member_import_template.csv'; a.click();
  }

  async function submit() {
    setErr('');
    if (method === 'email') {
      if (!name.trim() || !email.trim()) { setErr('Name and email are required'); return; }
      setBusy(true);
      try {
        const reqRef = doc(collection(db, 'access_requests'));
        await setDoc(reqRef, {
          uid: null, name: name.trim(), email: email.trim(),
          team: role, reason: `Added by ${ADMIN_NAME()}`,
          status: 'pre_approved', role,
          preApprovedEmail: email.trim().toLowerCase(),
          requestedAt: serverTimestamp(), approvedAt: serverTimestamp(),
          approvedBy: ADMIN_NAME(), addedBy: ADMIN_NAME(),
        });
        await writeLog('member_added', { action: 'member_added', email: email.trim(), role, addedBy: ADMIN_NAME() });
        onDone(`✓ ${name.trim()} pre-approved. Auto-activates on first sign-up.`);
      } catch (ex) { setErr(ex.message || 'Failed to add member'); setBusy(false); }

    } else if (method === 'pending') {
      if (selectedPending.size === 0) { setErr('Select at least one request'); return; }
      setBusy(true);
      try {
        for (const id of selectedPending) {
          const req = pending.find(r => r.id === id); if (!req) continue;
          const assignedRole = pendingRoles[id] || req.team || 'mp';
          await setDoc(doc(db, 'authorized_users', req.uid || id), {
            uid: req.uid || id, name: req.name, email: req.email,
            role: assignedRole, active: true,
            approvedAt: serverTimestamp(), approvedBy: ADMIN_NAME(), addedBy: ADMIN_NAME(),
          });
          await updateDoc(doc(db, 'access_requests', id), {
            status: 'approved', role: assignedRole, approvedAt: serverTimestamp(),
          });
          if (req.uid) {
            await addDoc(collection(db, 'notifications'), {
              recipientId: req.uid, type: 'approved',
              message: `You've been added to the ${roleLabel(assignedRole)} portal on Spacemancraft OS. Sign in to get started.`,
              createdAt: serverTimestamp(), read: false,
            });
          }
          await writeLog('approval', { email: req.email, role: assignedRole, adminAction: 'approved_via_member_panel', by: ADMIN_NAME() });
        }
        onDone(`✓ ${selectedPending.size} request(s) approved`);
      } catch (ex) { setErr(ex.message || 'Failed'); setBusy(false); }

    } else if (method === 'csv') {
      const validRows = csvRows.filter(r => r._valid);
      if (validRows.length === 0) { setErr('No valid rows to import'); return; }
      setBusy(true);
      try {
        for (const row of validRows) {
          const reqRef = doc(collection(db, 'access_requests'));
          await setDoc(reqRef, {
            uid: null, name: row.name, email: row.email, team: row.role,
            reason: `CSV import by ${ADMIN_NAME()}`,
            status: 'pre_approved', role: row.role,
            preApprovedEmail: row.email.toLowerCase(),
            requestedAt: serverTimestamp(), approvedAt: serverTimestamp(),
            approvedBy: ADMIN_NAME(), addedBy: ADMIN_NAME(),
          });
        }
        await bumpVersion(`Bulk import: ${validRows.length} members added`, ADMIN_NAME());
        onDone(`✓ ${validRows.length} member(s) imported via CSV`);
      } catch (ex) { setErr(ex.message || 'Import failed'); setBusy(false); }
    }
  }

  const METHODS = [{ id:'email', label:'By Email' }, { id:'pending', label:'From Requests' }, { id:'csv', label:'CSV Import' }];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(2,10,30,0.88)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: S.panel, border: '1px solid rgba(101,212,250,0.2)',
        width: '100%', maxWidth: 520, maxHeight: '88vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.75)' }}>

        {/* Header */}
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${S.faint}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: S.mono, fontSize: 14, color: S.primary, fontWeight: 700 }}>ADD MEMBER</div>
            <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: '0.1em', marginTop: 2 }}>PORTAL MEMBER MANAGEMENT</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        {/* Method selector */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${S.faint}`, flexShrink: 0 }}>
          {METHODS.map(m => (
            <button key={m.id} onClick={() => { setMethod(m.id); setErr(''); setCsvPreview(false); }}
              style={{ flex: 1, fontFamily: S.mono, fontSize: 10, padding: '10px',
                background: method === m.id ? 'rgba(101,212,250,0.07)' : 'transparent',
                border: 'none', borderBottom: `2px solid ${method === m.id ? S.primary : 'transparent'}`,
                color: method === m.id ? S.primary : S.dim, cursor: 'pointer',
                letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>

          {/* ── By email ── */}
          {method === 'email' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, lineHeight: 1.7 }}>
                A pre-approved slot is created. The account auto-activates when they sign up with this email address.
              </div>
              {[['Name *', name, setName, 'text', 'Full name'],
                ['Email address *', email, setEmail, 'email', 'team@spacemancraft.in']
              ].map(([label, val, setter, type, ph]) => (
                <div key={label}>
                  <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: '0.1em',
                    textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
                  <input value={val} onChange={e => setter(e.target.value)} type={type} placeholder={ph}
                    style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`,
                      color: S.text, fontFamily: S.mono, fontSize: 12,
                      padding: '10px 12px', boxSizing: 'border-box', outline: 'none' }} />
                </div>
              ))}
              <div>
                <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: '0.1em',
                  textTransform: 'uppercase', marginBottom: 5 }}>Assign to portal *</div>
                <div style={{ position: 'relative' }}>
                  <select value={role} onChange={e => setRole(e.target.value)}
                    style={{ width: '100%', appearance: 'none', background: S.input,
                      border: `1px solid ${S.border}`, color: roleColor(role),
                      fontFamily: S.mono, fontSize: 12, padding: '10px 30px 10px 12px',
                      boxSizing: 'border-box', outline: 'none', cursor: 'pointer' }}>
                    {roleOptions.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    color: S.dim, pointerEvents: 'none', fontSize: 12 }}>▾</span>
                </div>
              </div>
            </div>
          )}

          {/* ── From pending requests ── */}
          {method === 'pending' && (
            <div>
              <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, marginBottom: 14, lineHeight: 1.7 }}>
                Approve pending access requests and assign roles in bulk.
              </div>
              {pending.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', fontFamily: S.mono, fontSize: 11, color: S.dim }}>
                  No pending requests.
                </div>
              ) : pending.map(req => {
                const isSelected = selectedPending.has(req.id);
                return (
                  <div key={req.id} style={{ background: isSelected ? 'rgba(101,212,250,0.05)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${isSelected ? 'rgba(101,212,250,0.2)' : S.faint}`,
                    padding: '11px 12px', marginBottom: 7,
                    display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={isSelected}
                      onChange={() => {
                        const next = new Set(selectedPending);
                        if (isSelected) next.delete(req.id); else next.add(req.id);
                        setSelectedPending(next);
                      }}
                      style={{ accentColor: S.primary, cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: S.sans, fontSize: 12, fontWeight: 600, color: S.text }}>{req.name}</div>
                      <div style={{ fontFamily: S.mono, fontSize: 10, color: S.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.email}</div>
                      {req.reason && (
                        <div style={{ fontFamily: S.mono, fontSize: 9, color: '#334155', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          "{req.reason}"
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <select value={pendingRoles[req.id] || req.team || 'mp'}
                          onChange={e => setPendingRoles(p => ({ ...p, [req.id]: e.target.value }))}
                          style={{ appearance: 'none', background: S.input,
                            border: `1px solid ${S.border}`, color: S.muted,
                            fontFamily: S.mono, fontSize: 9, padding: '4px 22px 4px 8px',
                            cursor: 'pointer', outline: 'none' }}>
                          {roleOptions.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                        <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                          color: S.dim, fontSize: 10, pointerEvents: 'none' }}>▾</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── CSV import ── */}
          {method === 'csv' && !csvPreview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, lineHeight: 1.8 }}>
                Upload a CSV with columns: <strong style={{ color: S.text }}>name, email, role</strong>. One member per row.<br />
                Valid roles: {ALL_ROLES.slice(1).map(r => r.id).join(' · ')}
              </div>
              <button onClick={downloadTemplate}
                style={{ alignSelf: 'flex-start', background: 'transparent',
                  border: `1px solid ${S.border}`, color: S.dim, fontFamily: S.mono,
                  fontSize: 9, padding: '6px 14px', cursor: 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                ↓ Download Template
              </button>
              <div style={{ border: '2px dashed rgba(255,255,255,0.07)',
                padding: '36px', textAlign: 'center', cursor: 'pointer',
                transition: 'border-color 0.15s' }}
                onClick={() => fileRef.current?.click()}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(101,212,250,0.3)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}>
                <div style={{ fontFamily: S.mono, fontSize: 12, color: S.dim, marginBottom: 6 }}>
                  Click to select CSV file
                </div>
                <div style={{ fontFamily: S.mono, fontSize: 9, color: '#334155' }}>
                  or drag and drop
                </div>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleFile} />
            </div>
          )}

          {method === 'csv' && csvPreview && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontFamily: S.mono, fontSize: 10 }}>
                  <span style={{ color: S.green }}>{csvRows.filter(r => r._valid).length} valid</span>
                  {' · '}
                  <span style={{ color: '#f87171' }}>{csvRows.filter(r => !r._valid).length} invalid</span>
                </div>
                <button onClick={() => { setCsvPreview(false); setCsvRows([]); }}
                  style={{ background: 'none', border: 'none', color: S.dim, fontFamily: S.mono, fontSize: 9, cursor: 'pointer' }}>
                  ← Different file
                </button>
              </div>
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {csvRows.map((row, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 8, alignItems: 'center', padding: '7px 8px',
                    background: !row._valid ? 'rgba(248,113,113,0.06)' : i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                    border: `1px solid ${!row._valid ? 'rgba(248,113,113,0.15)' : S.faint}`, marginBottom: 3,
                  }}>
                    <span style={{ fontFamily: S.mono, fontSize: 10, color: !row._valid ? '#f87171' : S.green, flexShrink: 0, width: 14 }}>
                      {!row._valid ? '✗' : '✓'}
                    </span>
                    <span style={{ fontFamily: S.sans, fontSize: 12, color: S.text, flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.name || '—'}
                    </span>
                    <span style={{ fontFamily: S.mono, fontSize: 10, color: S.dim, flex: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.email || '—'}
                    </span>
                    <span style={{ fontFamily: S.mono, fontSize: 9, color: roleColor(row.role), flexShrink: 0 }}>{row.role || '—'}</span>
                    {!row._valid && (
                      <span style={{ fontFamily: S.mono, fontSize: 8, color: '#f87171', flexShrink: 0 }}>
                        {!row.name ? 'no name' : !row.email ? 'no email' : 'invalid role'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {err && (
            <div style={{ fontFamily: S.mono, fontSize: 10, color: '#f87171', marginTop: 14,
              padding: '8px 12px', background: 'rgba(248,113,113,0.08)',
              border: '1px solid rgba(248,113,113,0.2)' }}>{err}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${S.faint}`,
          flexShrink: 0, display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${S.border}`,
              color: S.dim, fontFamily: S.mono, fontSize: 10, padding: '11px',
              cursor: 'pointer', textTransform: 'uppercase' }}>
            CANCEL
          </button>
          <button onClick={submit} disabled={busy}
            style={{ flex: 2, background: '#0e9cc0', color: '#060b18', border: 'none',
              fontFamily: S.mono, fontSize: 10, fontWeight: 700, padding: '11px',
              cursor: busy ? 'wait' : 'pointer', textTransform: 'uppercase',
              letterSpacing: '0.1em', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'PROCESSING…'
              : method === 'email'   ? 'ADD MEMBER'
              : method === 'pending' ? `APPROVE (${selectedPending.size})`
              : `IMPORT (${csvRows.filter(r => r._valid).length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
